import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { z } from 'zod';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ClientAutocomplete } from '../../components/shared/ClientAutocomplete';
import { useCreateProject } from '../../hooks/useProjects';
import { useCreateClient, type ClientDTO } from '../../hooks/useClients';
import { useResidentes } from '../../hooks/useUsers';
import { useAuthStore } from '../../stores/auth-store';
import { toast } from '../../components/ui/Toast';

// Frontend form schema — matches createProjectSchema but client can be new
const formSchema = z
  .object({
    nombre: z.string().min(3, 'El nombre debe tener al menos 3 caracteres'),
    clientId: z.string().uuid('Seleccione un cliente').optional(),
    newClientNombre: z.string().optional(),
    rucCi: z.string().optional(),
    direccionObra: z.string().min(1, 'La dirección es requerida'),
    tieneGypsum: z.boolean().default(false),
    tienePintura: z.boolean().default(false),
    tieneImpermeabilizacion: z.boolean().default(false),
    tieneEmpaste: z.boolean().default(false),
    residenteId: z.string().uuid('Debe asignar un residente al proyecto'),
  })
  .refine(
    (data) => data.clientId || (data.newClientNombre && data.newClientNombre.length >= 3),
    { message: 'El nombre del cliente es requerido', path: ['clientId'] },
  )
  .refine(
    (data) => data.tieneGypsum || data.tienePintura || data.tieneImpermeabilizacion || data.tieneEmpaste,
    { message: 'Seleccione al menos un rubro para el proyecto', path: ['tieneGypsum'] },
  );

type FormValues = z.infer<typeof formSchema>;

export function NuevoProyectoPage() {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const createClient = useCreateClient();
  const user = useAuthStore((s) => s.user);
  const isResidente = user?.role === 'residente';
  const { data: residentes, isLoading: loadingResidentes } = useResidentes();

  const [selectedClient, setSelectedClient] = useState<ClientDTO | null>(null);
  const [isCreatingClient, setIsCreatingClient] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tieneGypsum: false,
      tienePintura: false,
      tieneImpermeabilizacion: false,
      tieneEmpaste: false,
      ...(isResidente && user ? { residenteId: user.id } : {}),
    },
  });

  const handleClientSelect = (client: ClientDTO | null) => {
    setSelectedClient(client);
    setIsCreatingClient(false);
    if (client) {
      setValue('clientId', client.id);
      setValue('newClientNombre', undefined);
    } else {
      setValue('clientId', undefined);
    }
  };

  const handleCreateNewClient = (nombre: string) => {
    setIsCreatingClient(true);
    setSelectedClient(null);
    setValue('clientId', undefined);
    setValue('newClientNombre', nombre);
  };

  const onSubmit = async (data: FormValues) => {
    try {
      let clientId = data.clientId;

      // Create client inline if needed
      if (!clientId && data.newClientNombre) {
        const newClient = await createClient.mutateAsync({
          nombre: data.newClientNombre,
          rucCi: data.rucCi || null,
        });
        clientId = newClient.id;
      }

      if (!clientId) return;

      const project = await createProject.mutateAsync({
        nombre: data.nombre,
        clientId,
        direccionObra: data.direccionObra,
        tieneGypsum: data.tieneGypsum,
        tienePintura: data.tienePintura,
        tieneImpermeabilizacion: data.tieneImpermeabilizacion,
        tieneEmpaste: data.tieneEmpaste,
        residenteId: data.residenteId,
      });

      navigate(`/proyectos/${project.id}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al crear el proyecto';
      toast('error', message);
    }
  };

  const residenteOptions = (residentes ?? []).map((r) => ({
    value: r.id,
    label: r.nombre,
  }));

  return (
    <div className="mx-auto max-w-2xl space-y-6 fade-up-1">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/proyectos')}
          className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-gray-800">Nuevo Proyecto</h1>
      </div>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 p-6">
          {/* 1. Nombre del proyecto (C-05: primer campo) */}
          <Input
            label="Nombre del proyecto"
            placeholder="Ej: Residencia H — Casa Cueva Lote 12"
            error={errors.nombre?.message}
            {...register('nombre')}
          />

          {/* 2. Nombre del cliente (autocomplete) */}
          <Controller
            name="clientId"
            control={control}
            render={() => (
              <ClientAutocomplete
                value={selectedClient}
                onChange={handleClientSelect}
                onCreateNew={handleCreateNewClient}
                error={errors.clientId?.message}
              />
            )}
          />

          {/* 3. RUC / Cédula (shown when creating new client) */}
          {isCreatingClient && (
            <Input
              label="RUC / Cédula"
              placeholder="0992345678001"
              {...register('rucCi')}
            />
          )}

          {/* 4. Dirección de la obra */}
          <Input
            label="Dirección de la obra"
            placeholder="Av. Principal y Calle 2, sector..."
            error={errors.direccionObra?.message}
            {...register('direccionObra')}
          />

          {/* 5. Rubros del proyecto */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Rubros del proyecto *
            </label>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue/30"
                  {...register('tieneGypsum')}
                />
                Gypsum
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue/30"
                  {...register('tienePintura')}
                />
                Pintura
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue/30"
                  {...register('tieneImpermeabilizacion')}
                />
                Impermeabilización
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-brand-blue focus:ring-brand-blue/30"
                  {...register('tieneEmpaste')}
                />
                Empaste
              </label>
            </div>
            {errors.tieneGypsum?.message && (
              <p className="mt-1 text-xs text-red-500">{errors.tieneGypsum.message}</p>
            )}
          </div>

          {/* 6. Residente asignado */}
          {isResidente ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Residente asignado
              </label>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {user?.nombre}
              </div>
              <input type="hidden" {...register('residenteId')} />
            </div>
          ) : (
            <Select
              label="Residente asignado"
              placeholder="Seleccionar residente..."
              options={residenteOptions}
              error={errors.residenteId?.message}
              disabled={loadingResidentes}
              {...register('residenteId')}
            />
          )}

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/proyectos')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting || createProject.isPending || createClient.isPending}
              loadingText="Creando proyecto..."
              fullWidth
            >
              Crear Proyecto
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
